import { Box, Button, Card, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Img } from "react-image";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { cacheService, thumbnailService } from "../../../Core/Api/services";

interface TitlePosterOrCoverCardProps {
  mode: "poster" | "cover";
  titleId: number;
  isAssigned: boolean;
  onClickReplace: () => void;
}

const TitlePosterOrCoverCard = (props: TitlePosterOrCoverCardProps) => {
  const { t } = useTranslation();
  const { mode, titleId, isAssigned, onClickReplace } = props;

  const imageUrl = React.useMemo(() => {
    return mode === "poster"
      ? thumbnailService.GetThumbRefByObjectId(titleId, cacheService.GetAuthKey()!, ThumbnailSize.small)
      : thumbnailService.GetThumbRefByObjectId(titleId, cacheService.GetAuthKey()!, ThumbnailSize.xxlarge);
  }, [mode]);

  return (
    <StyledCard isAssigned={isAssigned}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Stack spacing={1}>
          <Stack spacing={0.25}>
            <Typography variant="body1">
              {mode === "poster"
                ? t("card:titlePosterOrCoverCard.title.poster")
                : t("card:titlePosterOrCoverCard.title.cover")}
            </Typography>
            <Typography variant="body2" color="text.tier">
              {mode === "poster"
                ? t("card:titlePosterOrCoverCard.subtitle.poster")
                : t("card:titlePosterOrCoverCard.subtitle.cover")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button variant={isAssigned ? "outlined" : "contained"} onClick={onClickReplace}>
              {isAssigned
                ? t("card:titlePosterOrCoverCard.button.replace")
                : t("card:titlePosterOrCoverCard.button.assign")}
            </Button>
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.tier">
                {t("card:titlePosterOrCoverCard.resolution")}
              </Typography>
              <Typography variant="body2">{mode === "poster" ? "1920x2560" : "1920x1080"}</Typography>
            </Stack>
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.tier">
                {t("card:titlePosterOrCoverCard.ratio")}
              </Typography>
              <Typography variant="body2">{mode === "poster" ? "2:3" : "16:9"}</Typography>
            </Stack>
          </Stack>
        </Stack>
        {imageUrl && (
          <Box height={"100%"}>
            <Img
              src={imageUrl}
              loading="lazy"
              container={(children) => (
                <Box
                  sx={(t) => ({
                    aspectRatio: mode === "poster" ? "2 / 3" : "16 / 9",
                    height: 80,
                    img: { borderRadius: t.spacing(0.5), height: "100%", width: "100%", objectFit: "cover" },
                  })}
                >
                  {children}
                </Box>
              )}
            />
          </Box>
        )}
      </Stack>
    </StyledCard>
  );
};

export default TitlePosterOrCoverCard;

const StyledCard = styled(Card, { shouldForwardProp: (prop) => prop !== "isAssigned" })<{ isAssigned: boolean }>(
  ({ theme, isAssigned }) => {
    const borderColor = theme.palette.backgroundAlpha.border;
    const encodedColor = borderColor.replace("#", "%23"); // Encode '#' as '%23'

    return {
      padding: theme.spacing(2),
      width: "100%",
      border: isAssigned ? `1px solid ${theme.palette.backgroundAlpha.border}` : "none",
      background: theme.palette.background.default,
      "&:hover": {
        background: theme.palette.backgroundAlpha.hover,
      },
      ...(!isAssigned && {
        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='${encodedColor}' stroke-width='2' stroke-dasharray='6%2c14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        borderRadius: "6px",
      }),
    };
  }
);

const Picture = styled("figure", {
  shouldForwardProp: (prop) => true,
})<{}>(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 6,
  marginLeft: theme.spacing(2),
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: "none",
  backgroundColor: theme.palette.backgroundAlpha.main,
  img: {
    ...theme.helpers(theme).fullImage,
  },
  ".MuiSvgIcon-root": {
    fontSize: 40,
    color: theme.palette.text.tier,
  },
}));
